import {
  LOW_VOLUME_THRESHOLD,
  PAGE_SIZE,
  type Status,
  type Volume,
} from './issuersApi';

// The volume group an issuer ranks in within a status's listing — the
// volume param a fetchIssuerWithPosition rank needs to match a
// fetchListingPage view. undefined for 'funded' (un-split — see Volume).
export function volumeGroup(
  status: Status,
  totalPredictions: number | null | undefined,
): Volume | undefined {
  if (status === 'funded') return undefined;
  return (totalPredictions ?? 0) >= LOW_VOLUME_THRESHOLD ? 'high' : 'low';
}

// Window arithmetic over one fetchListingPage view: pagination, the
// high/low seam, and where the viewer's own row pins. Inputs must describe
// the SAME view (status/search/sort): listing is that view's
// fetchListingPage result (undefined while loading or errored), myVolume
// the viewer's volumeGroup, myGroupPosition the position from
// fetchIssuerWithPosition scoped by that volume. dividerIndex is the
// seam's index into the window's rows, −1 when either group is empty — an
// all-low listing deliberately gets no leading divider (it would sit above
// every row and read as broken). myPosition is the viewer's combined
// 1-based rank; pinSide is null when that rank falls inside the window
// (the row already appears among the listing rows). A 'low' viewer's pin
// is SKIPPED when the listing is unavailable — the combined rank needs
// highTotal, and pinning the bare group rank would display a wrong rank.
export function deriveListingView(params: {
  page: number; // 1-based, PAGE_SIZE rows per window
  listing: { total: number; highTotal: number; lowTotal: number } | undefined;
  myVolume: Volume | undefined;
  myGroupPosition: number | null | undefined;
}): {
  totalPages: number;
  pageStart: number;
  dividerIndex: number;
  myPosition: number | null;
  pinSide: 'top' | 'bottom' | null;
} {
  const { page, listing, myVolume, myGroupPosition } = params;
  const totalPages =
    listing ? Math.max(1, Math.ceil(listing.total / PAGE_SIZE)) : 1;
  const pageStart = (page - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const dividerIndex =
    listing && listing.highTotal > 0 && listing.lowTotal > 0 ?
      listing.highTotal - pageStart
    : -1;
  const positionBase = myVolume === 'low' ? listing?.highTotal : 0;
  const myRank = myGroupPosition ?? 0;
  const myPosition =
    myRank > 0 && positionBase !== undefined ? positionBase + myRank : null;
  const myIndex = myPosition === null ? null : myPosition - 1;
  const pinSide: 'top' | 'bottom' | null =
    myIndex === null ? null
    : myIndex < pageStart ? 'top'
    : myIndex >= pageEnd ? 'bottom'
    : null;
  return { totalPages, pageStart, dividerIndex, myPosition, pinSide };
}
